import { z } from 'zod';

const ALLOWED_SPECIAL_CHARS = '!"#$%&\'()*+,-./:;<=>?@[\\]^_`{|}~';

const PASSWORD_REGEX = new RegExp(
  // eslint-disable-next-line no-useless-escape
  `^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[!"#$%&'()*+,\\-./:;<=>?@[\\\\\\]^_\`{|}~])[A-Za-z\\d!"#$%&'()*+,\\-./:;<=>?@[\\\\\\]^_\`{|}~]{8,30}$`
);

export const createPasswordValidationSchema = (requireNameFields = true) => {
  const baseSchema = z.object({
    firstName: requireNameFields
      ? z.string().min(1, "First name can't be empty")
      : z.string().optional().default(''),
    lastName: requireNameFields
      ? z.string().min(1, "Last name can't be empty")
      : z.string().optional().default(''),
    password: z.string().refine((password) => PASSWORD_REGEX.test(password), {
      message: `Password must contain lowercase, uppercase, number, and only these special characters: ${ALLOWED_SPECIAL_CHARS}`,
    }),
    confirmPassword: z
      .string()
      .min(8, 'Password must be at least 8 characters long')
      .max(30, 'Password must not exceed 30 characters'),
  });

  return baseSchema.refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords must match',
    path: ['confirmPassword'],
  });
};

export type PasswordFormType = z.infer<ReturnType<typeof createPasswordValidationSchema>>;

export const passwordFormDefaultValues: PasswordFormType = {
  firstName: '',
  lastName: '',
  password: '',
  confirmPassword: '',
};

export const PASSWORD_REQUIREMENTS = [
  {
    key: 'length',
    label: 'Between 8 and 30 characters',
    regex: (password: string) => password.length >= 8 && password.length <= 30,
  },
  {
    key: 'case',
    label: 'At least 1 uppercase and 1 lowercase letter',
    regex: (password: string) => {
      return /[a-z]/.test(password) && /[A-Z]/.test(password);
    },
  },
  {
    key: 'number',
    label: 'At least 1 digit',
    regex: (password: string) => /\d/.test(password),
  },
  {
    key: 'special',
    label: `At least 1 special character(${ALLOWED_SPECIAL_CHARS.split('').join(' ')})`,
    regex: (password: string) => /[!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]/.test(password),
  },
];
